import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";
import { PublicDiscoveryViewPayloadType } from "../types/discovery";

/**
 * Discovery item type constants matching Django's DISCOVERY_VIEW_TYPE_MAP
 */
export const DISCOVERY_VIEW_TYPE = {
  EVENT: "event",
  MEMBERSHIP: "membership",
} as const;

/**
 * Minimal type for items that can have a path generated
 * Works with PublicDiscoveryViewSerializer and similar types
 */
type DiscoveryItemWithPath = {
  type?: string;
  slug?: string;
};

/**
 * Get the relative URL path for a discovery item (event or membership)
 *
 * This utility handles both events and memberships from the unified
 * discovery endpoint, generating the correct route based on type.
 *
 * @example
 * // Event
 * getDiscoveryItemPath({ type: 'event', slug: 'my-event' })
 * // Returns: "/my-event"
 *
 * @example
 * // Membership
 * getDiscoveryItemPath({ type: 'membership', slug: 'my-membership' })
 * // Returns: "/m/my-membership"
 *
 * @param item - Discovery item with type and slug
 * @returns Relative URL path for the item
 */
export function getDiscoveryItemPath(
  item: DiscoveryItemWithPath | null | undefined
): string {
  if (!item?.slug) {
    return "/";
  }

  if (item.type === DISCOVERY_VIEW_TYPE.MEMBERSHIP) {
    return PUBLIC_ROUTES.MEMBERSHIPS.detail(item.slug);
  }

  // Default to event route
  return PUBLIC_ROUTES.EVENTS.detail(item.slug);
}

const DEFAULT_CITY_RADIUS = "20";
const DEFAULT_SEARCH_QUERY_PARAMS: PublicDiscoveryViewPayloadType & {
  page_size?: number;
} = {
  payment_type: PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
  purchase_platform: enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
  page_size: 20,
};

// Used by queryClient to set and fetch query data between different files
// Params were put in this util file for reusability
export const getEventsDataParams = (
  cityLat: string | undefined,
  cityLong: string | undefined,
  cityRadius: number | undefined
) => {
  return {
    ...DEFAULT_SEARCH_QUERY_PARAMS,
    point_location: `${cityLat},${cityLong},${
      cityRadius ?? DEFAULT_CITY_RADIUS
    }`,
    page_size: 20,
    is_sponsored: false,
    is_featured: true,
  };
};

export const getSponsoredEventsParams = (
  cityLat: string | undefined,
  cityLong: string | undefined,
  greaterCityRadius: number | undefined,
  cityRadius: number | undefined
) => {
  return {
    ...DEFAULT_SEARCH_QUERY_PARAMS,
    point_location: `${cityLat},${cityLong},${
      greaterCityRadius ?? cityRadius ?? DEFAULT_CITY_RADIUS
    }`,
    is_sponsored: true,
    is_featured: true,
  };
};
